import React from 'react';
import { Box, CircularProgress, LinearProgress, Typography, Skeleton } from '@mui/material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';

const SpinnerContainer = styled(Box)(({ theme, variant }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),

  ...(variant === 'overlay' && {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(2px)',
    zIndex: 1000,
  }),

  ...(variant === 'fullscreen' && {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    zIndex: 9999,
  }),
}));

const StyledCircularProgress = styled(CircularProgress, {
  shouldForwardProp: (prop) => prop !== 'customColor',
})(({ theme, customColor }) => ({
  ...(customColor && {
    color: customColor,
  }),
}));

/**
 * Versatile loading spinner component with different variants and customization options
 *
 * @param {Object} props - Component props
 * @param {string} props.variant - Loading variant type
 * @param {string} props.size - Spinner size
 * @param {string} props.color - Spinner color
 * @param {string} props.text - Loading text
 * @param {boolean} props.showText - Show loading text
 * @param {number} props.thickness - Spinner thickness (1-8)
 * @param {number} props.value - Progress value (0-100) for determinate
 * @param {string} props.type - Progress type (indeterminate/determinate)
 * @param {number} props.minHeight - Minimum height for container
 * @param {boolean} props.fullWidth - Full width linear progress
 */
const LoadingSpinner = ({
  variant = 'default',
  size = 'medium',
  color = 'primary',
  text = 'Loading...',
  showText = true,
  thickness = 4,
  value,
  type = 'indeterminate',
  minHeight = 100,
  fullWidth = false,
  customColor,
  sx,
  ...otherProps
}) => {
  // Size mappings
  const sizeMap = {
    small: 24,
    medium: 40,
    large: 60,
    xl: 80,
  };

  const spinnerSize = typeof size === 'number' ? size : sizeMap[size];

  // Skeleton variant
  if (variant === 'skeleton') {
    return (
      <Box sx={{ width: '100%', ...sx }}>
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="40%" />
        <Skeleton variant="rectangular" height={118} sx={{ mt: 1 }} />
        <Box sx={{ pt: 0.5 }}>
          <Skeleton variant="text" width="80%" />
          <Skeleton variant="text" width="50%" />
        </Box>
      </Box>
    );
  }

  // Linear progress variant
  if (variant === 'linear') {
    return (
      <Box
        sx={{
          width: fullWidth ? '100%' : '200px',
          ...sx,
        }}
      >
        {showText && (
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            {text}
          </Typography>
        )}
        <LinearProgress
          variant={type}
          value={value}
          color={color}
          sx={{
            height: thickness,
            borderRadius: thickness / 2,
          }}
        />
        {type === 'determinate' && value !== undefined && (
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            {Math.round(value)}%
          </Typography>
        )}
      </Box>
    );
  }

  // Circular progress variants
  return (
    <SpinnerContainer
      variant={variant}
      sx={{
        minHeight: variant === 'inline' ? 'auto' : minHeight,
        ...sx,
      }}
    >
      <StyledCircularProgress
        size={spinnerSize}
        thickness={thickness}
        color={color === 'custom' ? undefined : color}
        customColor={color === 'custom' ? customColor : undefined}
        variant={type}
        value={value}
      />

      {showText && (
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{
            textAlign: 'center',
            maxWidth: '200px',
          }}
        >
          {text}
        </Typography>
      )}

      {type === 'determinate' && value !== undefined && (
        <Typography variant="body2" color="textSecondary">
          {Math.round(value)}%
        </Typography>
      )}
    </SpinnerContainer>
  );
};

LoadingSpinner.propTypes = {
  variant: PropTypes.oneOf(['default', 'overlay', 'fullscreen', 'inline', 'linear', 'skeleton']),
  size: PropTypes.oneOfType([
    PropTypes.oneOf(['small', 'medium', 'large', 'xl']),
    PropTypes.number,
  ]),
  color: PropTypes.oneOf([
    'primary',
    'secondary',
    'error',
    'warning',
    'info',
    'success',
    'inherit',
    'custom',
  ]),
  text: PropTypes.string,
  showText: PropTypes.bool,
  thickness: PropTypes.number,
  value: PropTypes.number,
  type: PropTypes.oneOf(['indeterminate', 'determinate']),
  minHeight: PropTypes.number,
  fullWidth: PropTypes.bool,
  customColor: PropTypes.string,
};

export default LoadingSpinner;
